import { Refine, Authenticated } from "@refinedev/core";
import routerBindings, { CatchAllNavigate } from "@refinedev/react-router";
import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation } from "react-router-dom";
import { lazy, Suspense } from "react";
import { dataProvider } from "@/providers/dataProvider";
import { liveProvider } from "@/providers/liveProvider";
import { authProvider } from "@/providers/authProvider";
import { accessControlProvider, roleCanSee, homeFor, isClientRole } from "@/providers/accessControl";
import { retryUnlessRefused } from "@/lib/retryPolicy";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ShortcutsProvider } from "@/hooks/useShortcuts";
import { PreferencesProvider } from "@/hooks/usePreferences";
import { BrandProvider } from "@/hooks/useBrand";
import { UndoManager } from "@/components/UndoManager";
import { SavedNotifier } from "@/components/SavedNotifier";
import { Toaster, notificationProvider } from "@/components/Toaster";
import { Login } from "@/pages/login";
import { ForceLogout } from "@/pages/logout";
import { TfaSetup } from "@/pages/tfa-setup";
import { ChangePassword } from "@/pages/change-password";
import { SecurityGate } from "@/components/SecurityGate";
// Every post-auth surface is lazy. For the staff modules that is not about load
// time: a client session must not download the staff nav or the staff pages,
// which carry internal route names, field names and page structure. The portal
// modules below are lazy for the ordinary reason — 24 of the 26 pages in this app
// are their own chunk, the two exceptions being `login` and `tfa-setup`, which are
// pre-auth and eager on purpose. An earlier version of this file kept the portal
// static so it "landed in the entry chunk", which stated the mechanism rather than
// a benefit and made the portal the only post-auth surface every staff user
// downloaded. The cost of the change is one request on an already-open connection
// the first time a client opens a portal route.
const PortalShell = lazy(() => import("@/components/PortalShell").then((m) => ({ default: m.PortalShell })));
const PortalProjects = lazy(() => import("@/pages/portal/projects").then((m) => ({ default: m.PortalProjects })));
const PortalProjectDetail = lazy(() => import("@/pages/portal/project-detail").then((m) => ({ default: m.PortalProjectDetail })));
const PortalConnections = lazy(() => import("@/pages/portal/connections").then((m) => ({ default: m.PortalConnections })));
const PortalInvite = lazy(() => import("@/pages/portal/invite").then((m) => ({ default: m.PortalInvite })));
const PortalPreviewPage = lazy(() =>
  import("@/pages/portal-preview").then((m) => ({ default: m.PortalPreviewPage })),
);
const PortalTaskDetail = lazy(() => import("@/pages/portal/task-detail").then((m) => ({ default: m.PortalTaskDetail })));
const Shell = lazy(() => import("@/components/Shell").then((m) => ({ default: m.Shell })));
const Dashboard = lazy(() => import("@/pages/dashboard").then((m) => ({ default: m.Dashboard })));
const Clients = lazy(() => import("@/pages/clients").then((m) => ({ default: m.Clients })));
const Routing = lazy(() => import("@/pages/routing").then((m) => ({ default: m.Routing })));
const ClientDetail = lazy(() => import("@/pages/workspace-detail").then((m) => ({ default: m.ClientDetail })));
const ProjectDetail = lazy(() => import("@/pages/workspace-detail").then((m) => ({ default: m.ProjectDetail })));
const Leads = lazy(() => import("@/pages/leads").then((m) => ({ default: m.Leads })));
const Contacts = lazy(() => import("@/pages/contacts").then((m) => ({ default: m.Contacts })));
const Meetings = lazy(() => import("@/pages/meetings").then((m) => ({ default: m.MeetingsPage })));
const ContactDetail = lazy(() => import("@/pages/contact-detail").then((m) => ({ default: m.ContactDetail })));
const LeadDetail = lazy(() => import("@/pages/lead-detail").then((m) => ({ default: m.LeadDetail })));
const Funding = lazy(() => import("@/pages/funding").then((m) => ({ default: m.Funding })));
const BucketDetail = lazy(() => import("@/pages/funding-detail").then((m) => ({ default: m.BucketDetail })));
const Billing = lazy(() => import("@/pages/billing").then((m) => ({ default: m.Billing })));
const Invoicing = lazy(() => import("@/pages/invoicing").then((m) => ({ default: m.Invoicing })));
const Tasks = lazy(() => import("@/pages/tasks").then((m) => ({ default: m.Tasks })));
const MyTasks = lazy(() => import("@/pages/my-tasks").then((m) => ({ default: m.MyTasks })));
const Expenses = lazy(() => import("@/pages/expenses").then((m) => ({ default: m.Expenses })));
const SubmitExpense = lazy(() => import("@/pages/submit-expense").then((m) => ({ default: m.SubmitExpense })));
const CloudCosts = lazy(() => import("@/pages/cloud").then((m) => ({ default: m.CloudCosts })));
const Reports = lazy(() => import("@/pages/reports").then((m) => ({ default: m.Reports })));
import { SettingsLayout, SettingsIndex } from "@/components/SettingsLayout";
const EmailSettings = lazy(() => import("@/pages/email-settings").then((m) => ({ default: m.EmailSettings })));
const AuditLog = lazy(() => import("@/pages/audit").then((m) => ({ default: m.AuditLog })));
const Access = lazy(() => import("@/pages/access").then((m) => ({ default: m.Access })));
const Connections = lazy(() => import("@/pages/connections").then((m) => ({ default: m.Connections })));
const Goat = lazy(() => import("@/pages/goat").then((m) => ({ default: m.Goat })));

function currentRole() {
  return localStorage.getItem("cordite_role") ?? "";
}

if (typeof window !== "undefined") {
  try {
    const role = currentRole();
    const hasTok = Boolean(localStorage.getItem("cordite_auth"));
    if (hasTok && isClientRole(role)) {
      void import("@/components/PortalShell");
      void import("@/pages/portal/projects");
    } else if (hasTok && (role === "Administrator" || role === "Engineer")) {
      void import("@/components/Shell");
      const p = window.location.pathname;
      if (p === "/" || p === "") void import("@/pages/dashboard");
      else if (p.startsWith("/tasks") || p.startsWith("/my-tasks")) void import("@/pages/tasks");
      else if (p.startsWith("/clients") || p.startsWith("/projects")) {
        void import("@/pages/clients");
        void import("@/pages/workspace-detail");
      } else if (p.startsWith("/contacts")) void import("@/pages/contacts");
    }
  } catch { /* ignore */ }
}

function RedirectKeepSearch({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

function RequireRole({ resource, children }: { resource: string; children: React.ReactNode }) {
  if (!roleCanSee(resource, currentRole())) return <Navigate to="/" replace />;
  return <>{children}</>;
}

// Guards the staff shell itself, not each route inside it: a client landing on a
// deep link is redirected before Shell mounts, so no staff module loads and no
// staff query is issued on their behalf.
function StaffOnly({ children }: { children: React.ReactNode }) {
  if (isClientRole(currentRole())) return <Navigate to="/portal" replace />;
  return <>{children}</>;
}

function PortalOnly({ children }: { children: React.ReactNode }) {
  if (!isClientRole(currentRole())) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function HomeForRole() {
  return <Navigate to={homeFor(currentRole())} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <Refine
        dataProvider={dataProvider}
        liveProvider={liveProvider}
        authProvider={authProvider}
        accessControlProvider={accessControlProvider}
        notificationProvider={notificationProvider}
        routerProvider={routerBindings}
        resources={[
          { name: "dashboard", list: "/" },
          { name: "clients", list: "/clients" },
          { name: "routing", list: "/settings/routing" },
          { name: "projects", list: "/projects" },
          { name: "leads", list: "/leads" },
          { name: "contacts", list: "/contacts" },
          { name: "my_tasks", list: "/my-tasks", meta: { label: "My Tasks" } },
          { name: "funding_buckets", list: "/funding", meta: { label: "Funding Buckets" } },
          { name: "billable_line_items", list: "/billing", meta: { label: "Billable Items" } },
          { name: "invoicing", list: "/invoicing", meta: { label: "Monthly Invoicing" } },
          { name: "tasks", list: "/tasks" },
          { name: "expenses", list: "/expenses" },
          { name: "submit_expense", list: "/submit-expense" },
          { name: "connections", list: "/connections" },
          { name: "cloud_imports", list: "/cloud", meta: { label: "Cloud Costs" } },
          { name: "cloud_allocations" },
          { name: "reports", list: "/reports" },
          { name: "audit", list: "/settings/audit" },
          { name: "access", list: "/settings/users", meta: { label: "Users" } },
          { name: "email_settings", list: "/settings/email", meta: { label: "Email" } },
          { name: "goat", list: "/goat", meta: { label: "G.O.A.T." } },
        ]}
        options={{
          liveMode: "auto",
          syncWithLocation: true,
          disableTelemetry: true,
          reactQuery: {
            clientConfig: {
              defaultOptions: {
                queries: {
                  retry: retryUnlessRefused,
                },
              },
            },
          },
        }}
      >
        <BrandProvider>
        <TooltipProvider delayDuration={200}>
        <PreferencesProvider>
        <ShortcutsProvider>
        <UndoManager />
        <SavedNotifier />
        <Toaster />
        <Suspense fallback={<div className="p-6 text-[13px] text-muted-foreground">Loading…</div>}>
        <Routes>
          {/*
            Invitation redemption sits outside EVERY <Authenticated> wrapper, deliberately.
            The person following this link has no account yet -- that is the entire point of
            the link -- so wrapping it in the "protected" pattern would bounce them to
            /login, and wrapping it in the inverted "auth-pages" pattern would bounce anyone
            who happens to still hold a staff session. Redemption has to work from any
            session state, including none.

            It also has to outrank `/portal/*` (the authenticated catch-all below), which it
            does: React Router v6 ranks static segments above splats across the whole
            <Routes> tree, so /portal/invite is matched here and never reaches that
            redirect. Verified in the browser, not only by reading the ranking rules.
          */}
          <Route path="/logout" element={<ForceLogout />} />
          <Route path="/portal/logout" element={<ForceLogout />} />
          <Route path="/portal/invite" element={<PortalInvite />} />
          <Route
            element={
              <Authenticated key="protected" fallback={<CatchAllNavigate to="/login" />}>
                <Outlet />
              </Authenticated>
            }
          >
            <Route path="/tfa-setup" element={<TfaSetup />} />
            <Route path="/change-password" element={<ChangePassword />} />
          </Route>
          <Route
            element={
              <Authenticated key="portal-shell" fallback={<CatchAllNavigate to="/login" />}>
                <PortalOnly>
                  <PortalShell>
                    <Outlet />
                  </PortalShell>
                </PortalOnly>
              </Authenticated>
            }
          >
            <Route path="/portal" element={<PortalProjects />} />
            <Route path="/portal/projects" element={<Navigate to="/portal" replace />} />
            <Route path="/portal/projects/:id" element={<PortalProjectDetail />} />
            <Route path="/portal/connections" element={<PortalConnections />} />
            {/*
              Flat, not nested under the project. The task id is globally unique,
              so a `:pid` segment would carry no addressing information -- it
              would be decoration that can disagree with the row, and it would
              invite the reader to believe the pid is checked. If it isn't, the
              URL lies; if it is, there are two authorization paths for one
              resource. A flat URL also survives a task moving project, where a
              nested one silently becomes wrong while still resolving.

              It has to outrank `/portal/*` below, and does: React Router ranks
              by segment specificity across the whole tree, not by source order,
              and a dynamic segment scores positively where a splat scores
              negatively. Note that the browser-verified precedent at line 129 is
              for a *static* path (`/portal/invite`); this is the adjacent
              static-plus-dynamic case, which is why 240-portal-task-surface-e2e
              loads this URL cold rather than trusting the ranking rules. Losing
              that match would not error -- the catch-all redirects -- so the
              symptom would be the project list appearing in place of the task.
            */}
            <Route path="/portal/tasks/:id" element={<PortalTaskDetail />} />
            <Route path="/portal/*" element={<Navigate to="/portal" replace />} />
          </Route>
          <Route
            element={
              <Authenticated key="preview-shell" fallback={<CatchAllNavigate to="/login" />}>
                <StaffOnly>
                  <Outlet />
                </StaffOnly>
              </Authenticated>
            }
          >
            <Route path="/portal/preview/:grantId" element={<PortalPreviewPage />} />
            <Route path="/portal/preview/client/:clientId" element={<PortalPreviewPage />} />
          </Route>
          <Route
            element={
              <Authenticated key="protected-shell" fallback={<CatchAllNavigate to="/login" />}>
                <StaffOnly>
                  <SecurityGate>
                    <Shell>
                      <Outlet />
                    </Shell>
                  </SecurityGate>
                </StaffOnly>
              </Authenticated>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/meetings" element={<Meetings />} />
            <Route path="/clients/:id" element={<ClientDetail />} />
            <Route path="/routing" element={<RedirectKeepSearch to="/settings/routing" />} />
            <Route path="/projects" element={<Navigate to="/clients" replace />} />
            <Route path="/projects/:id" element={<ProjectDetail />} />
            <Route path="/leads" element={<Leads />} />
            <Route path="/leads/:id" element={<LeadDetail />} />
            <Route path="/contacts" element={<Contacts />} />
            <Route path="/contacts/:id" element={<ContactDetail />} />
            <Route path="/funding" element={<RequireRole resource="funding_buckets"><Funding /></RequireRole>} />
            <Route path="/funding/:id" element={<RequireRole resource="funding_buckets"><BucketDetail /></RequireRole>} />
            <Route path="/billing" element={<RequireRole resource="billable_line_items"><Billing /></RequireRole>} />
            <Route path="/invoicing" element={<RequireRole resource="invoicing"><Invoicing /></RequireRole>} />
            <Route path="/tasks" element={<Tasks />} />
            <Route path="/my-tasks" element={<MyTasks />} />
            <Route path="/expenses" element={<RequireRole resource="expenses"><Expenses /></RequireRole>} />
            <Route path="/submit-expense" element={<SubmitExpense />} />
            <Route path="/connections" element={<Connections />} />
            <Route path="/connect" element={<Navigate to="/connections" replace />} />
            <Route path="/cloud" element={<RequireRole resource="cloud"><CloudCosts /></RequireRole>} />
            <Route path="/reports" element={<RequireRole resource="reports"><Reports /></RequireRole>} />
            <Route path="/audit" element={<RedirectKeepSearch to="/settings/audit" />} />
            <Route path="/settings" element={<SettingsLayout />}>
              <Route index element={<SettingsIndex />} />
              <Route path="users" element={<RequireRole resource="access"><Access /></RequireRole>} />
              <Route path="email" element={<RequireRole resource="email_settings"><EmailSettings /></RequireRole>} />
              <Route path="routing" element={<RequireRole resource="routing"><Routing /></RequireRole>} />
              <Route path="audit" element={<RequireRole resource="audit"><AuditLog /></RequireRole>} />
            </Route>
            <Route path="/users" element={<RedirectKeepSearch to="/settings/users" />} />
            <Route path="/access" element={<RedirectKeepSearch to="/settings/users" />} />
            <Route path="/goat" element={<Goat />} />
          </Route>
          <Route
            element={
              <Authenticated key="auth-pages" fallback={<Outlet />}>
                <HomeForRole />
              </Authenticated>
            }
          >
            <Route path="/login" element={<Login />} />
          </Route>
        </Routes>
        </Suspense>
        </ShortcutsProvider>
        </PreferencesProvider>
        </TooltipProvider>
        </BrandProvider>
      </Refine>
    </BrowserRouter>
  );
}
